import { useEffect, useRef } from 'react';
import { rafThrottle } from '../utils/rafThrottle';

const ACTIVE_TAB_SELECTOR = '[aria-selected="true"]';

const scrollToActiveTab = (container: HTMLDivElement | null) => {
  const activeTab = container?.querySelector<HTMLButtonElement>(ACTIVE_TAB_SELECTOR);
  if (!container || !activeTab) return;

  const containerRect = container.getBoundingClientRect();
  const activeTabRect = activeTab.getBoundingClientRect();

  const scrollLeft = container.scrollLeft;
  const offset = activeTabRect.left - containerRect.left;
  const centered = offset - (container.clientWidth / 2 - activeTab.clientWidth / 2);

  container.scrollTo({
    left: scrollLeft + centered,
    behavior: 'smooth',
  });
};

export const useScrollableTabs = () => {
  const tabListReference = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // 1. Scroll immediately after mounting
    scrollToActiveTab(tabListReference.current);

    // 2. Scroll when tab is active
    const observer = new MutationObserver(() => {
      scrollToActiveTab(tabListReference.current);
    });

    if (tabListReference.current) {
      observer.observe(tabListReference.current, {
        attributes: true,
        subtree: true,
      });
    }

    // 3. Add resize handler
    const throttledHandleResize = rafThrottle(() => {
      scrollToActiveTab(tabListReference.current);
    });

    window.addEventListener('resize', throttledHandleResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', throttledHandleResize);
    };
  }, []);

  return { tabListReference };
};
